import React, { RefObject } from "react";
import { ProductResponse } from "@/app/api/types/product";
import type { CustomEntity } from "@/app/api/types/preOrder";
import { ChevronDown, Pencil } from "lucide-react";
import { useTranslation } from "react-i18next";
import { UUID } from "crypto";
import type { StockSummary } from "@/query/hooks/useGlobalStocks";
import { getCurrencySymbol } from "@/lib/currency";
import { useUnitMap } from "@/query/hooks/useUnitMap";
import { useProductDisplayName } from "@/hooks/use-product-display-name";
import { useHoverTooltip } from "./useHoverTooltip";

interface ProductDisplayValueProps {
  value: UUID | null;
  disabled: boolean;
  isLastRow: boolean;
  isOpen: boolean;
  isCustomActive: boolean;
  currentProductDetails: ProductResponse | null;
  customProduct?: CustomEntity | null;
  placeholder?: string;
  priceLookup?: (productId: string) => number | null;
  /** Hide the inline price chip in the closed display (e.g. recap rows where
   *  the dedicated PriceCell already shows the value next to the product). */
  hideClosedPricePill?: boolean;
  stockLookup?: (productId: string) => StockSummary | null;
  stockSummary: { stocks: any } | null;
  skuInput: string;
  className: string;
  label?: string;
  required: boolean;
  // Handlers
  handleToggle: () => void;
  handleButtonKeyDown: (event: React.KeyboardEvent<HTMLButtonElement>) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  handleSkuChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleSkuBlur: () => void;
  handleSkuFocus: (e: React.FocusEvent<HTMLInputElement>) => void;
  setCustomDialogOpen: (open: boolean) => void;
  // Refs
  buttonRef: RefObject<HTMLButtonElement | null>;
  skuInputRef: RefObject<HTMLInputElement | null>;
}

export const ProductDisplayValue = ({
  value,
  disabled,
  isLastRow,
  isOpen,
  isCustomActive,
  currentProductDetails,
  customProduct,
  placeholder,
  priceLookup,
  hideClosedPricePill = false,
  stockLookup,
  stockSummary,
  skuInput,
  className,
  label,
  required,
  handleToggle,
  handleButtonKeyDown,
  handleKeyDown,
  handleSkuChange,
  handleSkuBlur,
  handleSkuFocus,
  setCustomDialogOpen,
  buttonRef,
  skuInputRef,
}: ProductDisplayValueProps) => {
  const { t } = useTranslation();

  const unitMap = useUnitMap();
  const displayName = useProductDisplayName();

  // The cell is intentionally too narrow to print either value in full — the
  // hover panels ARE the full value. Keep them cheap and fast (see
  // `useHoverTooltip`); suppress them while the dropdown is open so the panel
  // never fights the list for the same pixels.
  const fullName = isCustomActive
    ? (customProduct?.name ?? "")
    : (currentProductDetails?.name ?? currentProductDetails?.short_name ?? "");
  const fullSku = isCustomActive
    ? ""
    : (currentProductDetails?.sku ?? skuInput);

  const nameTooltip = useHoverTooltip(
    isOpen || !fullName ? null : (
      <>
        <span className="block break-words font-medium">{fullName}</span>
        {fullSku && (
          <span className="mt-0.5 block break-all text-muted-foreground tabular-nums">
            {fullSku}
          </span>
        )}
      </>
    ),
  );

  const skuTooltip = useHoverTooltip(
    isOpen || !fullSku ? null : (
      <>
        <span className="block break-all font-medium tabular-nums">
          {fullSku}
        </span>
        {fullName && (
          <span className="mt-0.5 block break-words text-muted-foreground">
            {fullName}
          </span>
        )}
      </>
    ),
  );

  return (
    <div className={`flex w-full min-w-0 items-center gap-1 ${className}`}>
      {label && (
        <label className="text-sm font-medium mb-2 block">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}

      {/* Chrome is kept to a minimum here on purpose: this cell's whole job is
          to show a SKU and a product name, so every gap / padding / reserved
          icon slot is width stolen from the two strings that matter. */}
      <div className="flex w-full min-w-0 items-center gap-1">
        {/* SKU Input Field */}
        <div className="w-16 min-w-[40px] flex-shrink-0">
          {isCustomActive ? (
            // Italic SKU placeholder — keep same border + padding as the
            // input/disabled variants so the row dimensions don't change
            // between custom-active / read-only / editable.
            <div className="w-full px-1 py-0.5 text-xs italic text-gray-400 border border-transparent rounded">
              -
            </div>
          ) : (
            // Single render. The `disabled` prop on <input> handles the
            // "can't click / can't type" behaviour; the visual chrome
            // (border, padding, font) stays identical regardless. Without
            // this, the previous disabled-branch swapped in a <div> with
            // different padding (py-1 vs py-0.5) and no border, so the
            // row shifted vertically every time the cell locked / unlocked.
            <input
              ref={skuInputRef}
              data-tab-cell
              data-testid="flow-sku-input"
              type="text"
              placeholder={t("ordersRecap.skuPlaceholder")}
              value={skuInput}
              disabled={disabled}
              {...skuTooltip.triggerProps}
              onClick={
                disabled
                  ? undefined
                  : (e) => {
                      e.currentTarget.select();
                    }
              }
              onFocus={disabled ? undefined : handleSkuFocus}
              onChange={disabled ? undefined : handleSkuChange}
              onKeyDown={disabled ? undefined : handleKeyDown}
              onBlur={disabled ? undefined : handleSkuBlur}
              readOnly={disabled}
              className={`w-full px-1 py-0.5 text-xs rounded transition-colors focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                isLastRow
                  ? "border border-gray-300"
                  : "border border-transparent group-hover/row:border-gray-400"
              } ${disabled ? "cursor-default bg-transparent text-gray-900" : ""}`}
            />
          )}
          {skuTooltip.tooltip}
        </div>

        <button
          ref={buttonRef}
          onClick={() => {
            handleToggle();
          }}
          onKeyDown={handleButtonKeyDown}
          {...nameTooltip.triggerProps}
          disabled={disabled}
          aria-expanded={isOpen}
          data-testid="product-select"
          data-tab-cell
          // Border is ALWAYS present (transparent when not styled) so the
          // button geometry doesn't shift between disabled and editable.
          // `disabled` only flips the cursor / hover affordance — never
          // the border / padding / size.
          className={`text-left flex-1 min-w-0 flex items-center px-1 py-0.5 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-gray-900 transition-colors ${
            disabled
              ? "border border-transparent cursor-default"
              : isCustomActive
                ? "border border-dashed border-orange-400 hover:border-orange-500 cursor-pointer"
                : isLastRow
                  ? "border border-gray-300 hover:border-blue-500 hover:text-blue-600 cursor-pointer"
                  : "border border-transparent group-hover/row:border-gray-400 hover:text-blue-600 cursor-pointer"
          }`}
          tabIndex={disabled ? -1 : 0}
        >
          <div className="flex-1 min-w-0 overflow-hidden flex items-center gap-1.5">
            <div className="min-w-0 flex-1">
              {isCustomActive ? (
                <>
                  <div className="text-xs min-w-0 truncate">
                    {customProduct!.name}
                  </div>
                  {(customProduct!.address_1 ||
                    customProduct!.code_postal ||
                    customProduct!.ville) && (
                    <div className="text-xs text-gray-600 truncate">
                      {[
                        customProduct!.address_1,
                        [customProduct!.code_postal, customProduct!.ville]
                          .filter(Boolean)
                          .join(" "),
                        customProduct!.pays,
                      ]
                        .filter(Boolean)
                        .join(", ")}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-xs min-w-0 truncate">
                  {displayName(currentProductDetails) ||
                    placeholder ||
                    t("purchases.selectProduct")}
                </div>
              )}
            </div>
            {/* Inline effective price — value on top, label below.
                Renders an invisible placeholder when no price so the row
                height stays constant whether or not pricing is present. */}
            {/* Reserve the chip slot whenever the lookup is wired,
                regardless of whether a product is selected. Empty
                product rows (the "add a row" placeholder at the
                bottom) previously collapsed because the chip was
                gated on currentProductDetails — they now keep the
                same height as data rows. */}
            {priceLookup && !hideClosedPricePill && (
              <span className="flex-shrink-0 flex flex-col items-end gap-px leading-none min-w-[2.25rem]">
                {(() => {
                  const price = currentProductDetails
                    ? priceLookup(currentProductDetails.id)
                    : null;
                  if (price == null) {
                    return (
                      <>
                        <span className="text-xs invisible">—</span>
                        <span className="text-[9px] invisible">—</span>
                      </>
                    );
                  }
                  const currency = getCurrencySymbol();
                  return (
                    <>
                      <span className="text-xs tabular-nums text-gray-700">
                        {Number(price).toFixed(2)}
                        {currency}
                      </span>
                      <span className="text-[9px] text-gray-400 uppercase font-medium tracking-wide">
                        {t("products.price")}
                      </span>
                    </>
                  );
                })()}
              </span>
            )}
            {stockLookup && (
              <span
                className={(() => {
                  const s = currentProductDetails
                    ? stockLookup(currentProductDetails.id)
                    : null;
                  if (s == null) {
                    return "flex-shrink-0 flex flex-col items-end gap-px leading-none px-1 py-0.5 min-w-[2.25rem]";
                  }
                  const qty = Math.round(s.quantity);
                  return `flex-shrink-0 flex flex-col items-end gap-px leading-none px-1 py-0.5 rounded min-w-[2.25rem] ${
                    qty <= 0 ? "bg-red-50" : "bg-green-50"
                  }`;
                })()}
              >
                {(() => {
                  const s = currentProductDetails
                    ? stockLookup(currentProductDetails.id)
                    : null;
                  if (s == null) {
                    return (
                      <>
                        <span className="text-xs invisible">—</span>
                        <span className="text-[9px] invisible">—</span>
                      </>
                    );
                  }
                  const qty = Math.round(s.quantity);
                  const unitAbbr = s.unit_id ? unitMap.get(s.unit_id) : null;
                  const valueColor =
                    qty <= 0 ? "text-red-600" : "text-green-700";
                  const labelColor =
                    qty <= 0 ? "text-red-400" : "text-green-600";
                  return (
                    <>
                      <span className={`text-xs tabular-nums ${valueColor}`}>
                        {qty}
                        {unitAbbr ? ` ${unitAbbr}` : ""}
                      </span>
                      <span
                        className={`text-[9px] uppercase font-medium tracking-wide ${labelColor}`}
                      >
                        {t("products.qty")}
                      </span>
                    </>
                  );
                })()}
              </span>
            )}
            {/* Stock availability display - only shown when lot_stock_enabled flag is true */}
            {!stockLookup && currentProductDetails && stockSummary?.stocks && (
              <span className="text-xs text-gray-500">
                Stock: {Number(stockSummary.stocks.total_weight).toFixed(2)} kg
              </span>
            )}
          </div>
          {isCustomActive && !disabled && (
            <span
              role="button"
              tabIndex={0}
              className="p-0.5 rounded border border-orange-300 bg-orange-50 hover:bg-orange-100 flex-shrink-0 ml-0.5 cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setCustomDialogOpen(true);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.stopPropagation();
                  setCustomDialogOpen(true);
                }
              }}
            >
              <Pencil className="h-3 w-3 text-orange-600" />
            </span>
          )}
          {/* The chevron is a hover affordance, so it must not COST anything
              when idle: it collapses to zero width (an svg clips to its own
              box) instead of merely fading out. Fading alone kept ~24px of
              permanently blank gutter in the narrowest column of the table.
              A resting row therefore spends its full width on name + SKU.
              Always visible for the last (add-a-row) line and while open —
              there is no product name competing for the space there. */}
          <ChevronDown
            className={`h-4 flex-shrink-0 transition-all duration-150 ${
              disabled
                ? "hidden"
                : isOpen
                  ? "w-4 ml-1 rotate-180 text-blue-600 opacity-100"
                  : isLastRow
                    ? "w-4 ml-1 text-gray-400 opacity-100"
                    : "w-0 ml-0 text-gray-400 opacity-0 group-hover/row:ml-1 group-hover/row:w-4 group-hover/row:opacity-100"
            }`}
          />
        </button>
        {nameTooltip.tooltip}
      </div>
    </div>
  );
};
